import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { api } from '@/lib/api';
import { useContent } from '@/lib/i18n';

/**
 * A module's name in the operator's language, from its id.
 *
 * **Why a hook and not four copies.** Six screens print a `moduleId` — the item
 * bank, the results list, the QC queue, the candidate drawer and two dashboard
 * hints — and every one of them was rendering the database key at an operator
 * who has been reading the name, in their own language, in that screen's own
 * module filter. The first fix inlined the same six-line lookup in three pages,
 * which is how a convention becomes three conventions.
 *
 * The query key is `['modules']`, the same one every module filter already uses,
 * so react-query serves this from cache rather than issuing a request per
 * screen. Adding it to a screen costs nothing.
 *
 * **An unknown id renders as itself**, which is what a soft-retired module that
 * has since been purged needs — and matches how `useLabel` treats a value it
 * does not recognise.
 */
export function useModuleName(): (moduleId: string | undefined | null) => string {
  const content = useContent();
  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules });

  return useCallback(
    (moduleId) => {
      if (!moduleId) return '';
      const found = (modules.data?.items ?? []).find((m) => m.moduleId === moduleId);
      return found ? content(found.name) : moduleId;
    },
    [modules.data, content],
  );
}
